import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import GratificacionesDetalle from '../components/GratificacionesDetalle';
import LiquidacionesDetalle from '../components/LiquidacionesDetalle';
import gratificacionService from '../services/gratificacionService';
import liquidacionService from '../services/liquidacionService';

const Calculos = () => {
  const { empresaId } = useParams();
  const { selectedCompany } = useAuth();
  
  const [activeTab, setActiveTab] = useState('gratificaciones');
  const [loading, setLoading] = useState(true);
  const [showGratificacionesDetalle, setShowGratificacionesDetalle] = useState(false);
  const [selectedPeriodoGratificacion, setSelectedPeriodoGratificacion] = useState(null);

  const [gratificacionesResumen, setGratificacionesResumen] = useState([]);
  const [ctsData, setCtsData] = useState([]);
  const [liquidaciones, setLiquidaciones] = useState([]);

  const [filters, setFilters] = useState({
    estado: 'all',
    dateType: 'all'
  });

  const [showModalNuevaGratificacion, setShowModalNuevaGratificacion] = useState(false);
  const [nuevaGratificacionPeriodo, setNuevaGratificacionPeriodo] = useState('Diciembre');
  const [nuevaGratificacionAnio, setNuevaGratificacionAnio] = useState(new Date().getFullYear());
  const [creandoGratificacion, setCreandoGratificacion] = useState(false);
  const [errorGratificacion, setErrorGratificacion] = useState(null);

  // Datos de prueba para CTS (períodos)
  const ctsTestData = [
    {
      id: 1,
      nombre: 'CTS Mayo - Octubre 2024',
      descripcion: 'Compensación por Tiempo de Servicios - Segundo Semestre',
      periodo: 'Mayo - Octubre 2024',
      fechaInicio: '2024-05-01',
      fechaFin: '2024-10-31',
      totalEmpleados: 18,
      totalCalculado: 25680.45,
      estado: 'proceso',
      fechaCalculo: '2024-10-15',
      fechaCreacion: '2024-10-15'
    },
    {
      id: 2,
      nombre: 'CTS Noviembre 2023 - Abril 2024',
      descripcion: 'Compensación por Tiempo de Servicios - Primer Semestre',
      periodo: 'Noviembre 2023 - Abril 2024',
      fechaInicio: '2023-11-01',
      fechaFin: '2024-04-30',
      totalEmpleados: 16,
      totalCalculado: 22150.80,
      estado: 'cerrada',
      fechaCalculo: '2024-04-15',
      fechaCreacion: '2024-04-15'
    },
    {
      id: 3,
      nombre: 'CTS Mayo - Octubre 2023',
      descripcion: 'Compensación por Tiempo de Servicios - Segundo Semestre',
      periodo: 'Mayo - Octubre 2023',
      fechaInicio: '2023-05-01',
      fechaFin: '2023-10-31',
      totalEmpleados: 14,
      totalCalculado: 18950.60,
      estado: 'cerrada',
      fechaCalculo: '2023-10-15',
      fechaCreacion: '2023-10-15'
    }
  ];

  // Datos de prueba para liquidaciones (períodos de liquidaciones procesadas)
  const liquidacionesTestData = [
    {
      id: 1,
      nombre: 'Liquidaciones Diciembre 2024',
      descripcion: 'Procesamiento de liquidaciones de beneficios sociales',
      fechaInicio: '2024-12-01',
      fechaFin: '2024-12-31',
      totalEmpleados: 3,
      totalCalculado: 28450.90,
      estado: 'proceso',
      fechaCalculo: '2024-12-15',
      fechaCreacion: '2024-12-15',
      detalle: 'Liquidaciones por cese de personal'
    },
    {
      id: 2,
      nombre: 'Liquidaciones Noviembre 2024',
      descripcion: 'Procesamiento de liquidaciones de beneficios sociales',
      fechaInicio: '2024-11-01',
      fechaFin: '2024-11-30',
      totalEmpleados: 2,
      totalCalculado: 15680.75,
      estado: 'cerrada',
      fechaCalculo: '2024-11-30',
      fechaCreacion: '2024-11-30',
      detalle: 'Liquidaciones por término de contrato'
    },
    {
      id: 3,
      nombre: 'Liquidaciones Octubre 2024',
      descripcion: 'Procesamiento de liquidaciones de beneficios sociales',
      fechaInicio: '2024-10-01',
      fechaFin: '2024-10-31',
      totalEmpleados: 1,
      totalCalculado: 12350.40,
      estado: 'cerrada',
      fechaCalculo: '2024-10-31',
      fechaCreacion: '2024-10-31',
      detalle: 'Liquidación por renuncia voluntaria'
    }
  ];

  const tabs = [
    { id: 'gratificaciones', name: 'Gratificaciones', icon: '🎁', color: 'text-green-600' },
    { id: 'cts', name: 'CTS', icon: '🏦', color: 'text-blue-600' },
    { id: 'liquidaciones', name: 'Liquidaciones', icon: '📋', color: 'text-red-600' }
  ];

  const idEmpresa = empresaId || selectedCompany?.id;

  const loadGratificaciones = useCallback(async () => {
    if (!idEmpresa) {
      setGratificacionesResumen([]);
      return;
    }
    try {
      const res = await gratificacionService.list(idEmpresa);
      const list = (res.data || []).map((g) => ({
        id: g.id,
        gratificacionId: g.id,
        año: g.anio,
        periodo: g.periodo_nombre || (g.periodo === 'junio' ? 'Junio' : 'Diciembre'),
        nombre: g.nombre,
        descripcion: g.descripcion || '',
        fechaInicio: g.fecha_inicio,
        fechaFin: g.fecha_fin,
        totalEmpleados: g.total_empleados ?? 0,
        totalCalculado: g.total_calculado ?? 0,
        estado: g.estado === 'cerrada' ? 'cerrada' : 'proceso',
      }));
      setGratificacionesResumen(list);
    } catch (err) {
      console.error('Error cargando gratificaciones:', err);
      setGratificacionesResumen([]);
    }
  }, [idEmpresa]);

  const loadLiquidaciones = useCallback(async () => {
    if (!idEmpresa) {
      setLiquidaciones([]);
      return;
    }
    try {
      const res = await liquidacionService.list(idEmpresa);
      setLiquidaciones(res.data || []);
    } catch (err) {
      console.error('Error cargando liquidaciones:', err);
      setLiquidaciones([]);
    }
  }, [idEmpresa]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (idEmpresa) {
        await loadGratificaciones();
        await loadLiquidaciones();
      } else {
        setGratificacionesResumen([]);
        setLiquidaciones([]);
      }
      setCtsData(ctsTestData);
      setLoading(false);
    };
    loadData();
  }, [idEmpresa, loadGratificaciones, loadLiquidaciones]);

  // Al cambiar a la pestaña Liquidaciones, refrescar lista para que las estadísticas estén al día
  useEffect(() => {
    if (idEmpresa && activeTab === 'liquidaciones') loadLiquidaciones();
  }, [idEmpresa, activeTab, loadLiquidaciones]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  // Funciones de utilidad (similar a Planillas)
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getEstadoBadge = (estado) => {
    const estadoConfig = {
      proceso: { class: 'bg-blue-100 text-blue-800', text: 'En Proceso', icon: '⚙️' },
      cerrada: { class: 'bg-green-100 text-green-800', text: 'Cerrada', icon: '✅' },
      anulada: { class: 'bg-red-100 text-red-800', text: 'Anulada', icon: '❌' }
    };
    return estadoConfig[estado] || estadoConfig.proceso;
  };

  const handleVerDetalleGratificacion = (doc) => {
    setSelectedPeriodoGratificacion({
      ...doc,
      gratificacionId: doc.gratificacionId ?? doc.id,
    });
    setShowGratificacionesDetalle(true);
  };

  const handleVolverFromDetalle = () => {
    setShowGratificacionesDetalle(false);
    setSelectedPeriodoGratificacion(null);
  };

  const handleCerrarDocumento = async (id, tipo) => {
    try {
      if (tipo === 'gratificacion') {
        await gratificacionService.cerrar(id);
        await loadGratificaciones();
        alert('Gratificación cerrada exitosamente');
      } else if (tipo === 'cts') {
        setCtsData(prev => prev.map(item => 
          item.id === id ? { ...item, estado: 'cerrada' } : item
        ));
        alert('CTS cerrada exitosamente');
      } else if (tipo === 'liquidacion') {
        setLiquidaciones(prev => prev.map(item => 
          item.id === id ? { ...item, estado: 'cerrada' } : item
        ));
        alert('Liquidación cerrada exitosamente');
      }
    } catch (error) {
      console.error(`Error al cerrar ${tipo}:`, error);
      alert(error.response?.data?.message || `Error al cerrar ${tipo}`);
    }
  };

  const handleBorrarGratificacion = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar la gratificación "${nombre || 'esta gratificación'}"? Esta acción no se puede deshacer.`)) return;
    try {
      await gratificacionService.delete(id);
      if (selectedPeriodoGratificacion?.gratificacionId === id) {
        setShowGratificacionesDetalle(false);
        setSelectedPeriodoGratificacion(null);
      }
      await loadGratificaciones();
      alert('Gratificación eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar gratificación:', error);
      alert(error.response?.data?.message || 'Error al eliminar la gratificación');
    }
  };

  const handleAbrirDocumento = async (id, tipo) => {
    try {
      if (tipo === 'gratificacion') {
        await gratificacionService.abrir(id);
        await loadGratificaciones();
        alert('Gratificación reabierta exitosamente');
      } else if (tipo === 'cts') {
        setCtsData(prev => prev.map(item => 
          item.id === id ? { ...item, estado: 'proceso' } : item
        ));
        alert('CTS reabierta exitosamente');
      } else if (tipo === 'liquidacion') {
        setLiquidaciones(prev => prev.map(item => 
          item.id === id ? { ...item, estado: 'proceso' } : item
        ));
        alert('Liquidación reabierta exitosamente');
      }
    } catch (error) {
      console.error(`Error al abrir ${tipo}:`, error);
      alert(error.response?.data?.message || `Error al abrir ${tipo}`);
    }
  };

  const handleCrearGratificacion = async () => {
    if (!idEmpresa) return;
    setCreandoGratificacion(true);
    setErrorGratificacion(null);
    try {
      const res = await gratificacionService.create(idEmpresa, nuevaGratificacionPeriodo, nuevaGratificacionAnio);
      setShowModalNuevaGratificacion(false);
      await loadGratificaciones();
      if (res.data?.id) {
        const g = res.data;
        setSelectedPeriodoGratificacion({
          gratificacionId: g.id,
          id: g.id,
          periodo: g.periodo_nombre || (g.periodo === 'junio' ? 'Junio' : 'Diciembre'),
          año: g.anio,
          nombre: g.nombre,
          fechaInicio: g.fecha_inicio,
          fechaFin: g.fecha_fin,
          totalEmpleados: g.total_empleados,
          totalCalculado: g.total_calculado,
          estado: g.estado === 'cerrada' ? 'cerrada' : 'proceso',
        });
        setShowGratificacionesDetalle(true);
      }
      alert('Gratificación creada correctamente');
    } catch (err) {
      console.error('Error creando gratificación:', err);
      setErrorGratificacion(err.response?.data?.message || 'Error al crear gratificación');
    } finally {
      setCreandoGratificacion(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Obtener datos filtrados según tab activo
  const getCurrentData = () => {
    let data = [];
    if (activeTab === 'gratificaciones') data = gratificacionesResumen;
    else if (activeTab === 'cts') data = ctsData;
    else if (activeTab === 'liquidaciones') data = liquidaciones;
    
    return data.filter(item => {
      if (filters.estado === 'all') return true;
      return item.estado === filters.estado;
    });
  };

  const filteredData = getCurrentData();

  // Estadísticas adaptadas por tab
  const getStats = () => {
    if (activeTab === 'gratificaciones') {
      const d = gratificacionesResumen;
      return {
        tipo: 'gratificaciones',
        total: d.length,
        proceso: d.filter(p => p.estado === 'proceso').length,
        cerrada: d.filter(p => p.estado === 'cerrada').length,
        montoTotal: d.reduce((s, p) => s + (Number(p.totalCalculado) || 0), 0),
      };
    }
    if (activeTab === 'cts') {
      const d = ctsData;
      return {
        tipo: 'cts',
        total: d.length,
        proceso: d.filter(p => p.estado === 'proceso').length,
        cerrada: d.filter(p => p.estado === 'cerrada').length,
        montoTotal: d.reduce((s, p) => s + (Number(p.totalCalculado) || 0), 0),
      };
    }
    // Liquidaciones: no tienen estado proceso/cerrada, son registros generados
    const d = liquidaciones;
    const anioActual = new Date().getFullYear();
    return {
      tipo: 'liquidaciones',
      total: d.length,
      montoTotal: d.reduce((s, p) => s + (Number(p.total_liquidacion) || 0), 0),
      esteAnio: d.filter(p => {
        const f = p.fecha_generacion || p.created_at;
        return f && new Date(f).getFullYear() === anioActual;
      }).length,
    };
  };

  const stats = getStats();

  // Si no hay empresa seleccionada
  if (!selectedCompany) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para ver los cálculos laborales.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:space-y-4 px-1 sm:px-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-8 space-y-4 lg:space-y-0">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">🧮 Cálculos Laborales</h1>
          <p className="text-gray-600">Gestión de gratificaciones, CTS y liquidaciones</p>
          {selectedCompany && (
            <p className="text-sm text-gray-500">Empresa: {selectedCompany.nombre_empresa}</p>
          )}
        </div>
        <button
          onClick={() => activeTab === 'gratificaciones' ? setShowModalNuevaGratificacion(true) : null}
          className="flex items-center justify-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 shrink-0"
          disabled={activeTab !== 'gratificaciones' || !idEmpresa}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nuevo Cálculo</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? `${tab.color} border-current`
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Statistics Cards — adaptadas por pestaña */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {stats.tipo === 'cts' ? (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 sm:col-span-2 lg:col-span-3 flex items-center justify-center">
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 text-blue-600 mb-3">
                <span className="text-2xl">🏦</span>
              </div>
              <p className="text-gray-600 font-medium">Estadísticas de CTS</p>
              <p className="text-gray-500 text-sm mt-1">Próximamente</p>
            </div>
          </div>
        ) : stats.tipo === 'liquidaciones' ? (
          <>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-red-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">Liquidaciones generadas</p>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-green-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">Monto total liquidado</p>
                  <p className="text-lg lg:text-2xl font-bold text-green-600">{formatCurrency(stats.montoTotal)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200 sm:col-span-2 lg:col-span-1">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-amber-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">Generadas este año</p>
                  <p className="text-lg lg:text-2xl font-bold text-amber-600">{stats.esteAnio}</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-blue-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">
                    {stats.tipo === 'gratificaciones' ? 'Periodos de gratificación' : 'Períodos CTS'}
                  </p>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-blue-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">En proceso</p>
                  <p className="text-lg lg:text-2xl font-bold text-blue-600">{stats.proceso}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-green-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">Cerradas</p>
                  <p className="text-lg lg:text-2xl font-bold text-green-600">{stats.cerrada}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Filtros — solo para Gratificaciones y CTS (listas con estado) */}
      {activeTab !== 'liquidaciones' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
              <select
                value={filters.estado}
                onChange={(e) => setFilters(prev => ({ ...prev, estado: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Todos los estados</option>
                <option value="proceso">En Proceso</option>
                <option value="cerrada">Cerrada</option>
                <option value="anulada">Anulada</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por</label>
              <select
                value={filters.dateType}
                onChange={(e) => setFilters(prev => ({ ...prev, dateType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Todos</option>
                <option value="year">Por Año</option>
                <option value="month">Por Mes</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ estado: 'all', dateType: 'all' })}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Documentos */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 lg:p-6">
        {showGratificacionesDetalle ? (
          <GratificacionesDetalle
            empresaId={empresaId || selectedCompany?.id}
            selectedPeriodo={selectedPeriodoGratificacion}
            onVolver={handleVolverFromDetalle}
          />
        ) : activeTab === 'cts' ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <span className="text-2xl">🏦</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">CTS</h3>
            <p className="text-gray-500 text-sm">Próximamente</p>
          </div>
        ) : activeTab === 'liquidaciones' ? (
          <LiquidacionesDetalle
            empresaId={empresaId || selectedCompany?.id}
            onVolver={() => setActiveTab('gratificaciones')}
            onLiquidacionesChange={loadLiquidaciones}
          />
        ) : (
          <>
            {filteredData.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay documentos</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Comienza creando un nuevo {activeTab === 'gratificaciones' ? 'cálculo de gratificaciones' : 
                                            activeTab === 'cts' ? 'cálculo de CTS' : 
                                            'cálculo de liquidación'}.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => activeTab === 'gratificaciones' ? setShowModalNuevaGratificacion(true) : null}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Nuevo Cálculo
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Documento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Período
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Empleados
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredData.map((documento) => {
                        const estadoConfig = getEstadoBadge(documento.estado);
                        return (
                          <tr key={documento.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                                  <span className="text-white font-bold text-sm">
                                    {documento.nombre.charAt(0)}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{documento.nombre}</div>
                                  <div className="text-xs text-gray-500">{documento.descripcion}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>{formatDateToDDMMYYYY(documento.fechaInicio)}</div>
                              <div className="text-xs text-gray-500">{formatDateToDDMMYYYY(documento.fechaFin)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {documento.totalEmpleados} empleados
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                              {formatCurrency(documento.totalCalculado)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                                <span className="mr-1">{estadoConfig.icon}</span>
                                {estadoConfig.text}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex space-x-2">
                                {activeTab === 'gratificaciones' && (
                                  <button
                                    onClick={() => handleVerDetalleGratificacion(documento)}
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                    title="Ver Detalle"
                                  >
                                    Ver Detalle
                                  </button>
                                )}
                                {documento.estado === 'proceso' && (
                                  <button
                                    onClick={() => handleCerrarDocumento(documento.id, activeTab === 'gratificaciones' ? 'gratificacion' : activeTab === 'cts' ? 'cts' : 'liquidacion')}
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                    title="Cerrar Documento"
                                  >
                                    Cerrar
                                  </button>
                                )}
                                {documento.estado === 'cerrada' && (
                                    <button
                                    onClick={() => handleAbrirDocumento(documento.id, activeTab === 'gratificaciones' ? 'gratificacion' : activeTab === 'cts' ? 'cts' : 'liquidacion')}
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition-colors"
                                    title="Abrir Documento"
                                  >
                                    Abrir
                                  </button>
                                )}
                                {activeTab === 'gratificaciones' && documento.estado === 'proceso' && (
                                  <button
                                    onClick={() => handleBorrarGratificacion(documento.id, documento.nombre)}
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                    title="Borrar gratificación"
                                  >
                                    Borrar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden divide-y divide-gray-200">
                  {filteredData.map((documento) => {
                    const estadoConfig = getEstadoBadge(documento.estado);
                    return (
                      <div key={documento.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {documento.nombre.charAt(0)}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{documento.nombre}</div>
                              <div className="text-xs text-gray-500">{documento.descripcion}</div>
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                            <span className="mr-1">{estadoConfig.icon}</span>
                            {estadoConfig.text}
                          </span>
                        </div>
                        
                        <div className="mt-3 text-sm text-gray-600">
                          <div>Período: {formatDateToDDMMYYYY(documento.fechaInicio)} - {formatDateToDDMMYYYY(documento.fechaFin)}</div>
                          <div>Empleados: {documento.totalEmpleados} • Total: {formatCurrency(documento.totalCalculado)}</div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          {activeTab === 'gratificaciones' && (
                            <button
                              onClick={() => handleVerDetalleGratificacion(documento)}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              Ver Detalle
                            </button>
                          )}
                          {documento.estado === 'proceso' && (
                            <button
                              onClick={() => handleCerrarDocumento(documento.id, activeTab === 'gratificaciones' ? 'gratificacion' : activeTab === 'cts' ? 'cts' : 'liquidacion')}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                            >
                              Cerrar
                            </button>
                          )}
                          {documento.estado === 'cerrada' && (
                            <button
                              onClick={() => handleAbrirDocumento(documento.id, activeTab === 'gratificaciones' ? 'gratificacion' : activeTab === 'cts' ? 'cts' : 'liquidacion')}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition-colors"
                            >
                              Abrir
                            </button>
                          )}
                          {activeTab === 'gratificaciones' && documento.estado === 'proceso' && (
                            <button
                              onClick={() => handleBorrarGratificacion(documento.id, documento.nombre)}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                            >
                              Borrar
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Modal Nueva Gratificación */}
      {showModalNuevaGratificacion && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => !creandoGratificacion && setShowModalNuevaGratificacion(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Nuevo cálculo de gratificación</h3>
              {errorGratificacion && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errorGratificacion}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
                  <select
                    value={nuevaGratificacionPeriodo}
                    onChange={(e) => setNuevaGratificacionPeriodo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Junio">Junio (Fiestas Patrias)</option>
                    <option value="Diciembre">Diciembre (Navidad)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                  <select
                    value={nuevaGratificacionAnio}
                    onChange={(e) => setNuevaGratificacionAnio(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModalNuevaGratificacion(false)}
                  disabled={creandoGratificacion}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCrearGratificacion}
                  disabled={creandoGratificacion}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50"
                >
                  {creandoGratificacion ? 'Creando...' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calculos;
